import { InputItem, Toggle } from "pepsico-ds";
import PropTypes from "prop-types";

/**
 * Renders a single field within the configuration drawer.
 * Supports text, number, email, toggle, and read-only display modes.
 */
const DrawerField = ({
  field,
  value,
  error,
  mode,
  onChange,
  onToggleChange,
  disabled,
}) => {
  const isViewMode = mode === "view";
  const isReadOnly = isViewMode || field.readOnly;

  if (field.type === "toggle") {
    return (
      <div className="config-drawer-field">
        {isReadOnly ? (
          <div className="config-drawer-field view">
            <span className="config-drawer-field-label">{field.label}</span>
            <span className="config-drawer-field-value">
              {value ? "Enabled" : "Disabled"}
            </span>
          </div>
        ) : (
          <div className="config-drawer-field-header">
            <span className="config-drawer-field-label">{field.label}</span>

            <Toggle
              checked={!!value}
              onUpdate={(checked) => onToggleChange(field.path, checked)}
              size="medium"
            />
          </div>
        )}
      </div>
    );
  }

  if (isReadOnly) {
    return (
      <div className="config-drawer-field view">
        <label className="config-drawer-field-label">{field.label}:</label>
        <span className="config-drawer-field-value">
          {value !== undefined && value !== null ? value.toString() : "-"}
        </span>
      </div>
    );
  }

  return (
    <div className="config-drawer-field">
      <label className="config-drawer-field-label" htmlFor={field.path}>
        <span>{field.label}</span>
        {field.required && (
          <span className="config-drawer-field-required">*Required</span>
        )}
      </label>
      <InputItem
        label={field.label}
        placeholder={field.placeholder || ""}
        value={value !== undefined && value !== null ? value.toString() : ""}
        onChange={(e) => onChange(field.path, e.target.value)}
        size="medium"
        type={field.type === "number" ? "number" : "text"}
        error={!!error}
        helperText={error || ""}
        disabled={disabled}
      />
    </div>
  );
};

DrawerField.propTypes = {
  field: PropTypes.shape({
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    required: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.string,
  }).isRequired,
  value: PropTypes.any,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  onChange: PropTypes.func,
  onToggleChange: PropTypes.func,
  disabled: PropTypes.bool,
};

/**
 * Renders a list of fields based on field definitions.
 * Groups fields by section if specified.
 */
const DrawerFieldRenderer = ({
  fields,
  formData,
  fieldErrors,
  mode,
  onChange,
  onToggleChange,
  getDeepValue,
}) => {
  // Group fields by section
  const groupedFields = fields.reduce((groups, field) => {
    const section = field.section || "__default__";
    if (!groups[section]) groups[section] = [];
    groups[section].push(field);
    return groups;
  }, {});

  const resolveDisabled = (field) => {
    if (!field.dependsOn) return false;
    const dependencyValue = getDeepValue(formData, field.dependsOn);
    return !dependencyValue;
  };

  return (
    <div className="config-drawer-fields">
      {Object.entries(groupedFields).map(([section, sectionFields]) => (
        <div key={section} className="config-drawer-section">
          {section !== "__default__" && (
            <p className="config-drawer-section-title">{section}</p>
          )}
          {sectionFields.map((field) => {
            const value = getDeepValue(formData, field.path);
            const error = getDeepValue(fieldErrors, field.path);

            return (
              <DrawerField
                key={field.path}
                field={field}
                value={value}
                error={error}
                mode={mode}
                onChange={onChange}
                onToggleChange={onToggleChange}
                disabled={resolveDisabled(field)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

DrawerFieldRenderer.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      section: PropTypes.string,
    })
  ).isRequired,
  formData: PropTypes.object.isRequired,
  fieldErrors: PropTypes.object,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  onChange: PropTypes.func,
  onToggleChange: PropTypes.func,
  getDeepValue: PropTypes.func.isRequired,
};

export default DrawerFieldRenderer;
